import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Alert, Typography} from '@mui/material';
import FormDialog from '../../../Dialog/FormDialog';
import {useFormSubmit} from '@alchemy/api';
import CollectionTreeWidget from '../../../Form/CollectionTreeWidget';
import {copyAssets} from '../../../../api/collection';
import {
    FormFieldErrors,
    FormRow,
    RemoteErrors,
    SwitchWidget,
} from '@alchemy/react-form';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {Asset} from '../../../../types';
import AssetSelection from '../../../AssetList/AssetSelection';
import {StackedModalProps, useModals} from '@alchemy/navigation';
import {useDirtyFormPrompt} from '@alchemy/phrasea-framework';
import {toast} from 'react-toastify';
import {OnSelectionChange} from '../../../AssetList/types';
import {WorkspaceOrCollectionTreeItem} from '../../Collection/CollectionTree/types.ts';

function AssetList({
    assets,
    onSelectionChange,
}: {
    assets: Asset[];
    onSelectionChange: OnSelectionChange<Asset>;
}) {
    const {t} = useTranslation();

    return (
        <div>
            <Typography variant={'body1'}>
                {t(
                    'form.copy_assets.asset_not_linkable.select_for_hard_copy',
                    `You can select the asset you want to duplicate to the destination (hard copy):`
                )}
            </Typography>
            <div
                style={{
                    height: '50vh',
                }}
            >
                <AssetSelection
                    assets={assets}
                    onSelectionChange={onSelectionChange}
                />
            </div>
        </div>
    );
}

type FormData = {
    destination: string | WorkspaceOrCollectionTreeItem;
    byReference: boolean;
    withAttributes: boolean;
    withTags: boolean;
};

type Props = {
    assets: Asset[];
    onComplete: () => void;
} & StackedModalProps;

export default function CopyAssetsDialog({
    assets,
    onComplete,
    ...modalProps
}: Props) {
    const [workspaceDest, setWorkspaceDest] = useState<string>();
    const {t} = useTranslation();
    const {closeModal} = useModals();
    const [selectionOW, setSelectionOW] = useState<Asset[]>([]);
    const [selectionP, setSelectionP] = useState<Asset[]>([]);

    const count = assets.length;

    const {
        handleSubmit,
        control,
        watch,
        formState: {errors},
        remoteErrors,
        submitting,
        forbidNavigation,
    } = useFormSubmit({
        defaultValues: {
            destination: '',
            byReference: true,
            withAttributes: true,
            withTags: true,
        },
        onSubmit: (data: FormData) => {
            const finalSelection: string[] = [
                ...assets
                    .filter(
                        a =>
                            a.capabilities.share &&
                            workspaceDest &&
                            (!data.byReference ||
                                a.workspace.id === workspaceDest)
                    )
                    .map(a => a.id),
                ...selectionOW.map(a => a.id),
                ...selectionP.map(a => a.id),
            ];

            return copyAssets(
                finalSelection,
                (data.destination as WorkspaceOrCollectionTreeItem).id!,
                data.byReference,
                {
                    withAttributes: data.withAttributes,
                    withTags: data.withTags,
                }
            );
        },
        onSuccess: () => {
            toast.success(
                t('copy_assets_dialog.assets_were_copied', `Assets were copied`)
            );
            closeModal();
            onComplete();
        },
    });
    useDirtyFormPrompt(forbidNavigation, modalProps.modalIndex);
    const byRef = watch('byReference');

    const nonLinkablePerm: Asset[] = useMemo(
        () => (byRef ? assets.filter(a => !a.capabilities.edit) : []),
        [byRef, assets]
    );
    const nonLinkableToOtherWS: Asset[] = useMemo(
        () =>
            byRef
                ? assets.filter(
                      a =>
                          a.capabilities.edit &&
                          workspaceDest &&
                          a.workspace.id !== workspaceDest
                  )
                : [],
        [workspaceDest, nonLinkablePerm]
    );

    const formId = 'copy-assets';

    return (
        <FormDialog
            {...modalProps}
            title={t('copy_assets.dialog.title', {
                defaultValue: `Copy {{count}} asset`,
                defaultValue_other: `Copy {{count}} assets`,
                count,
            })}
            loading={submitting}
            formId={formId}
            submitIcon={<FileCopyIcon />}
            submitLabel={t('copy_assets.dialog.submit', 'Copy')}
        >
            <Typography sx={{mb: 3}}>
                {t(
                    'copy_assets.dialog.intro',
                    'Where do you want to copy the selected assets?'
                )}
            </Typography>
            <form id={formId} onSubmit={handleSubmit}>
                <FormRow>
                    <SwitchWidget
                        control={control}
                        name={'byReference'}
                        label={t(
                            'copy_assets.form.by_reference.label',
                            'Copy by reference (shortcut)'
                        )}
                    />
                </FormRow>
                <div
                    style={{
                        display: byRef ? 'none' : 'block',
                    }}
                >
                    <FormRow>
                        <SwitchWidget
                            disabled={byRef}
                            control={control}
                            name={'withAttributes'}
                            label={t(
                                'copy_assets.form.with_attributes.label',
                                'Copy attributes'
                            )}
                        />
                    </FormRow>
                    <FormRow>
                        <SwitchWidget
                            disabled={byRef}
                            control={control}
                            name={'withTags'}
                            label={t(
                                'copy_assets.form.with_tags.label',
                                'Copy tags'
                            )}
                        />
                    </FormRow>
                </div>
                <FormRow>
                    <CollectionTreeWidget
                        isSelectable={node =>
                            node.data.capabilities.createAsset
                        }
                        onChange={node => {
                            setWorkspaceDest(node?.data?.workspaceId);
                        }}
                        control={control}
                        name={'destination'}
                        rules={{
                            required: true,
                        }}
                        label={t(
                            'form.copy_assets.destination.label',
                            'Destination'
                        )}
                    />
                </FormRow>

                {nonLinkablePerm.length > 0 && (
                    <Alert
                        severity={'warning'}
                        sx={{
                            'flexGrow': 1,
                            '.MuiAlert-message': {
                                flexGrow: 1,
                            },
                        }}
                    >
                        <Typography variant={'body1'}>
                            {t(
                                'form.copy_assets.asset_not_linkable.permission',
                                `The following assets cannot be copied by reference because you don't have sufficient permission.`
                            )}
                        </Typography>
                        <AssetList
                            onSelectionChange={setSelectionP}
                            assets={nonLinkablePerm}
                        />
                    </Alert>
                )}
                {nonLinkableToOtherWS.length > 0 && (
                    <Alert severity={'warning'}>
                        <Typography variant={'body1'}>
                            {t(
                                'form.copy_assets.asset_not_linkable.other_ws',
                                `The following assets cannot be copied by reference in another workspace.`
                            )}
                        </Typography>
                        <AssetList
                            onSelectionChange={setSelectionOW}
                            assets={nonLinkableToOtherWS}
                        />
                    </Alert>
                )}

                <FormFieldErrors field={'destination'} errors={errors} />
            </form>
            <RemoteErrors errors={remoteErrors} />
        </FormDialog>
    );
}
